import React, { useState, useMemo, useEffect } from "react";
import SortData from "./SortData";
import { PopoverButton } from "@headlessui/react";
import FilterData from "./FilterData";
import { useAuth } from "../context/AuthContext";
import { API_BASE } from "../utils/Instance";
import { toast } from "sonner";

const ContestTable3 = ({ role, data: initialData, setContests }) => {
  const [filterValue, setFilterValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const itemsPerPage = 6;

  const [sortSelected, setSortSelected] = useState("");
  const [filter, setFilter] = useState(false);
  const [selectedContest, setSelectedContest] = useState(null);
  const [contestResults, setContestResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [selectedVoterResult, setSelectedVoterResult] = useState(null);
  const { profile } = useAuth();

  const normalizeVoters = (voters = []) =>
    voters.map((voter) => {
      if (typeof voter === "string") {
        return {
          screen_name: voter,
          total_votes: 1,
        };
      }

      return {
        screen_name: voter?.screen_name || voter?.name || voter?.username || "Unknown User",
        total_votes: voter?.total_votes || voter?.votes || 0,
      };
    });

  // Check if screen is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    // Check on mount
    checkIfMobile();

    // Add event listener
    window.addEventListener("resize", checkIfMobile);

    // Clean up
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Fetch contest results
  const handleViewResults = async (contest) => {
    // Only fetch if contest has ended
    const endTime = contest.end_time ? new Date(contest.end_time) : null;
    const now = new Date();
    if (endTime && now < endTime) {
      toast.info("Contest has not ended yet. Results will be available soon.");
      return;
    }

    setSelectedContest(contest);
    setLoadingResults(true);
    
    try {
      const response = await fetch(
        `${API_BASE}accounts/votes/contest_results/?contest_id=${contest.id}`,
        {
          headers: {
            'Authorization': `Bearer ${profile?.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch contest results');
      }
      
      const data = await response.json();
      setContestResults(data);
    } catch (error) {
      console.error('Error fetching contest results:', error);
      setContestResults({ results: [] });
    } finally {
      setLoadingResults(false);
    }
  };

  const closeModal = () => {
    setSelectedContest(null);
    setContestResults(null);
    setSelectedVoterResult(null);
  };

  // Close expanded voters when modal is closed
  useEffect(() => {
    if (!selectedContest) {
      setSelectedVoterResult(null);
    }
  }, [selectedContest]);

  // Filtering functionality
  const filteredData = useMemo(() => {
    if (!filterValue) return initialData;

    return initialData.filter((item) => {
      const searchStr = filterValue.toLowerCase();
      return (
        (item.title || item.name || "").toLowerCase().includes(searchStr) ||
        (item.category || "").toLowerCase().includes(searchStr)
      );
    });
  }, [initialData, filterValue]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const handleFilterChange = (e) => {
    setFilterValue(e.target.value);
    setCurrentPage(1);
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleDelete = (contestId) => {
    setContests((prev) => prev.filter((contest) => contest.id !== contestId));
  };

  const isContestEnded = (endTime) => {
    return new Date(endTime) < new Date();
  };

  const getStatusStyle = (endTime) => {
    const ended = isContestEnded(endTime);
    return ended
      ? "px-3 py-1 rounded-full text-sm bg-green-500/20 text-green-500 border border-green-500"
      : "px-3 py-1 rounded-full text-sm bg-yellow-500/20 text-yellow-500 border border-yellow-500";
  };

  const getStatusText = (endTime) => {
    return isContestEnded(endTime) ? "Completed" : "Active";
  };

  const handleSort = (value) => {
    setSortSelected(value);
    // Implement sorting logic here
    console.log("Sorting data...", value);
  };

  // Mobile card view
  const renderMobileView = () => {
    return (
      <div className="space-y-4">
        {paginatedData.map((contest, index) => (
          <div
            key={`contest-${index}`}
            className="bg-[#1C1C1E] rounded-lg p-4 text-white"
          >
            <h3 className="font-semibold text-lg mb-3">{contest.title}</h3>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-400">ID:</span>
                <span>{contest.id}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-400">Category:</span>
                <span>{contest.category}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-400">Status:</span>
                <span className={getStatusStyle(contest.end_time)}>
                  {getStatusText(contest.end_time)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-400">End Date:</span>
                <span>{contest.end_time ? new Date(contest.end_time).toLocaleDateString() : "N/A"}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-400">Participants:</span>
                <span>{contest.participants_count || 0}</span>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              {role === "admin" && (
                <button
                  onClick={() => handleDelete(contest.id)}
                  className="p-2 border border-[#EA4335] rounded"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#EA4335"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 6h18"></path>
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                  </svg>
                </button>
              )}
              <button
                onClick={() => handleViewResults(contest)}
                className="px-4 py-2 rounded-lg font-semibold bg-[#ceb46a] text-black hover:bg-[#e3c97a] transition"
              >
                View Results
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Desktop table view
  const renderDesktopView = () => {
    return (
      <div className="overflow-hidden rounded-lg">
        <table className="w-full">
          <thead>
            <tr className="bg-[#ceb46a] text-black">
              <th className="py-4 px-6 text-left">Contest ID</th>
              <th className="py-4 px-6 text-left">Name</th>
              <th className="py-4 px-6 text-left">Category</th>
              <th className="py-4 px-6 text-left">Status</th>
              <th className="py-4 px-6 text-left">End Date</th>
              <th className="py-4 px-6 text-left">Participants</th>
              <th className="py-4 px-6 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((contest, index) => (
              <tr
                key={`contest-${index}`}
                className={
                  index % 2 === 0
                    ? "bg-[#161617] text-white"
                    : "bg-[#1C1C1E] text-white"
                }
              >
                <td className="py-4 px-6">{contest.id}</td>
                <td className="py-4 px-6">{contest.title}</td>
                <td className="py-4 px-6">{contest.category}</td>
                <td className="py-4 px-6">
                  <span className={getStatusStyle(contest.end_time)}>
                    {getStatusText(contest.end_time)}
                  </span>
                </td>
                <td className="py-4 px-6">
                  {contest.end_time ? new Date(contest.end_time).toLocaleDateString() : "N/A"}
                </td>
                <td className="py-4 px-6">{contest.participants_count || 0}</td>
                <td className="py-4 px-6">
                  <div className="flex gap-2">
                    {role === "admin" && (
                      <button
                        onClick={() => handleDelete(contest.id)}
                        className="p-2 border border-[#EA4335] rounded hover:bg-[#EA4335] hover:bg-opacity-10 transition"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#EA4335"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => handleViewResults(contest)}
                      className="px-4 py-2 rounded-lg font-semibold bg-[#ceb46a] text-black hover:bg-[#e3c97a] transition"
                    >
                      View Results
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="w-full mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-4">
        <h2 className="text-xl font-semibold mb-2 sm:mb-0 font-inter sm:mr-3 text-[#ceb46a]">
          Contest History
        </h2>
        <div className="relative flex-grow max-w-sm w-full">
          <input
            type="text"
            placeholder="Search"
            value={filterValue}
            onChange={handleFilterChange}
            className="pl-10 pr-4 py-2 rounded-full border border-[#ceb46a] bg-[#1C1C1E] text-[#667080] w-full"
          />
          <svg
            className="absolute left-3 top-2.5 text-gray-400"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ceb46a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </div>
        <div className="flex gap-2 mt-2 sm:mt-0">
          <button
            onClick={() => setFilter(true)}
            className={"p-2 border border-[#ceb46a] rounded outline-0"}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ceb46a"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
            </svg>
          </button>
          <SortData
            triggerButton={
              <PopoverButton
                className={"p-2 border border-[#ceb46a] rounded outline-0"}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ceb46a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="4" y1="6" x2="20" y2="6"></line>
                  <line x1="4" y1="12" x2="20" y2="12"></line>
                  <line x1="4" y1="18" x2="20" y2="18"></line>
                </svg>
              </PopoverButton>
            }
            options={[
              { value: "newest", label: "Max entrants first" },
              { value: "oldest", label: "Lowest entrants first" },
              { value: "random", label: "Random" },
            ]}
            value={sortSelected}
            onChange={handleSort}
          />
        </div>
      </div>
      <FilterData isOpen={filter} setIsOpen={setFilter} />
      {isMobile ? renderMobileView() : renderDesktopView()}

      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-2">
        <div className="text-sm text-gray-600">
          Page {currentPage} of {totalPages || 1}
        </div>
        <div className="flex gap-2">
          <button
            className="px-4 py-1 border border-[#667080] text-[#667080] rounded flex items-center gap-1 disabled:opacity-50"
            onClick={previousPage}
            disabled={currentPage <= 1}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            Previous
          </button>
          <button
            className="px-4 py-1 border border-[#667080] text-[#667080] rounded flex items-center gap-1 disabled:opacity-50"
            onClick={nextPage}
            disabled={currentPage >= totalPages}
          >
            Next
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      </div>

      {/* Results Modal */}
      {selectedContest && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50"
          onClick={() => {
            setSelectedVoterResult(null);
          }}
        >
          <div
            className="bg-[#1C1C1E] text-white rounded-2xl shadow-lg w-3/4 max-w-3xl p-6"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
          >
            <h2 className="text-2xl font-bold text-[#ceb46a] mb-4">
              Full Results – {selectedContest.title || selectedContest.name}
            </h2>

            {loadingResults ? (
              <div className="py-10 text-center text-gray-400">
                Loading results...
              </div>
            ) : (
              <div className="overflow-y-auto max-h-96 custom-scrollbar">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-[#ceb46a] text-black">
                      <th className="py-3 px-4 text-left">Place</th>
                      <th className="py-3 px-4 text-left">Contributor Screenname</th>
                      <th className="py-3 px-4 text-left">Total Votes</th>
                      <th className="py-3 px-4 text-left">Voter Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(contestResults?.results || []).map((result, idx) => {
                      const voters = normalizeVoters(result.voters || []);

                      return (
                        <tr
                          key={result.participant_id}
                          className={
                            idx % 2 === 0 ? "bg-[#161617]" : "bg-[#1C1C1E]"
                          }
                        >
                          <td className="py-3 px-4 font-bold">
                            {result.place === 1 && "🥇 "}
                            {result.place === 2 && "🥈 "}
                            {result.place === 3 && "🥉 "}
                            {result.place}
                          </td>
                          <td className="py-3 px-4">{result.screen_name}</td>
                          <td className="py-3 px-4">{result.total_votes}</td>
                          <td className="py-3 px-4">
                            <button
                              className="px-3 py-1 rounded bg-[#ceb46a] text-black font-semibold hover:bg-[#b99d55] transition duration-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedVoterResult({
                                  contributor: result.screen_name,
                                  voters,
                                });
                              }}
                            >
                              View ({voters.length})
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {(!contestResults || contestResults.results?.length === 0) && (
                      <tr>
                        <td colSpan="4" className="py-6 text-center text-gray-400">
                          No results available yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {selectedVoterResult && (
              <div
                className="fixed inset-0 z-[60] bg-black bg-opacity-70 flex items-center justify-center px-4"
                onClick={() => setSelectedVoterResult(null)}
              >
                <div
                  className="bg-[#1C1C1E] border border-[#ceb46a] text-white rounded-lg shadow-2xl w-full max-w-lg p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-start justify-between gap-4 mb-5">
                    <h3 className="text-xl font-bold text-[#ceb46a]">
                      Voters for {selectedVoterResult.contributor}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setSelectedVoterResult(null)}
                      className="text-gray-400 hover:text-white text-2xl leading-none"
                      aria-label="Close voter details"
                    >
                      &times;
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#ceb46a] text-black">
                          <th className="py-3 px-4 text-left">User Screenname</th>
                          <th className="py-3 px-4 text-right">Total Votes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedVoterResult.voters.length > 0 ? (
                          selectedVoterResult.voters.map((voter, voterIdx) => (
                            <tr
                              key={`${voter.screen_name}-${voterIdx}`}
                              className={voterIdx % 2 === 0 ? "bg-[#161617]" : "bg-[#232326]"}
                            >
                              <td className="py-3 px-4">{voter.screen_name}</td>
                              <td className="py-3 px-4 text-right font-semibold">
                                {voter.total_votes}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="2" className="py-6 text-center text-gray-400">
                              No voters yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={closeModal}
                className="px-5 py-2 rounded-lg font-semibold
                           bg-[#ceb46a] text-black
                           hover:bg-[#e3c97a] transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContestTable3;
